import { FormEvent, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, type Branch, type Employee } from "@/lib/api";

export default function EmployeesPage() {
  const qc = useQueryClient();
  const list = useQuery<Employee[]>({
    queryKey: ["md", "employees"],
    queryFn: api.listEmployees,
  });
  const branches = useQuery<Branch[]>({
    queryKey: ["md", "branches"],
    queryFn: api.listBranches,
  });

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [branchId, setBranchId] = useState("");
  const [roleHint, setRoleHint] = useState("");
  const [error, setError] = useState<string | null>(null);

  const branchById = useMemo(() => {
    const m = new Map<string, string>();
    branches.data?.forEach((b) => m.set(b.id, b.name));
    return m;
  }, [branches.data]);

  const create = useMutation({
    mutationFn: () =>
      api.createEmployee({
        name,
        primary_email: email || null,
        branch_id: branchId || null,
        role_hint: roleHint || null,
      }),
    onSuccess: () => {
      setName("");
      setEmail("");
      setRoleHint("");
      setError(null);
      qc.invalidateQueries({ queryKey: ["md", "employees"] });
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.detail ?? err.message : String(err)),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!name) return;
    create.mutate();
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-brand-900">Employees</h1>
        <p className="text-sm text-slate-600 mt-1">
          People who act inside source systems — salespeople, technicians,
          support agents. Used for top-performer leaderboards later.
        </p>
      </header>

      <section className="bg-white rounded-lg border shadow-sm p-5">
        <h2 className="font-medium">Add an employee</h2>
        <form onSubmit={submit} className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name"
            required
            className="rounded-md border px-3 py-2 text-sm"
          />
          <input
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            placeholder="Email (optional)"
            className="rounded-md border px-3 py-2 text-sm"
          />
          <select
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            className="rounded-md border px-3 py-2 text-sm bg-white"
          >
            <option value="">Branch (optional)</option>
            {branches.data?.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <input
            value={roleHint}
            onChange={(e) => setRoleHint(e.target.value)}
            placeholder="Role hint (sales/tech/support)"
            className="rounded-md border px-3 py-2 text-sm"
          />
          <button
            type="submit"
            disabled={create.isPending || !name}
            className="bg-brand-600 hover:bg-brand-700 text-white text-sm rounded-md px-3 py-2 disabled:opacity-60 md:col-span-1"
          >
            {create.isPending ? "Saving…" : "Create"}
          </button>
        </form>
        {error && <p className="mt-2 text-sm text-rose-700">{error}</p>}
      </section>

      <section className="bg-white rounded-lg border shadow-sm p-5">
        <h2 className="font-medium">Existing employees</h2>
        {list.data && list.data.length === 0 && (
          <p className="text-sm text-slate-500 mt-2">No employees yet.</p>
        )}
        {list.data && list.data.length > 0 && (
          <table className="mt-3 w-full text-sm">
            <thead className="text-slate-500 border-b">
              <tr>
                <th className="text-left py-2">Name</th>
                <th className="text-left py-2">Email</th>
                <th className="text-left py-2">Branch</th>
                <th className="text-left py-2">Role hint</th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((e) => (
                <tr key={e.id} className="border-b last:border-none">
                  <td className="py-2">{e.name}</td>
                  <td className="py-2">{e.primary_email ?? "—"}</td>
                  <td className="py-2">
                    {e.branch_id ? branchById.get(e.branch_id) ?? e.branch_id : "—"}
                  </td>
                  <td className="py-2">{e.role_hint ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
